/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import findIndex from 'lodash/findIndex';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import toArray from 'lodash/toArray';
import toString from 'lodash/toString';
import size from 'lodash/size';
import cloneDeep from 'lodash/cloneDeep';
import join from 'lodash/join';
import toNumber from 'lodash/toNumber';
import camelCase from 'lodash/camelCase';

export const isRomanNumeral = (value) => {
    return (
        !isNil(value) &&
        /^(M{1,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})|M{0,4}(CM|C?D|D?C{1,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})|M{0,4}(CM|CD|D?C{0,3})(XC|X?L|L?X{1,3})(IX|IV|V?I{0,3})|M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|I?V|V?I{1,3}))$/.test(
            value
        )
    );
};

const preserveCapitalLetters = (str) => {
    const isAllCapital = str === str.toUpperCase();

    if (isAllCapital) return str.toLowerCase();
    return str;
};

export function toTitleCase(str) {
    if (str) {
        if (includes(str, "'") || includes(str, '-')) {
            // handle strings with apostrophe
            return str.replace(
                /(?=\S*['])([a-zA-Z']+)/g,
                (txt) =>
                    txt.charAt(0).toUpperCase() + preserveCapitalLetters(txt.substr(1))
            );
        }
        return str.replace(
            /[A-Za-zÀ-ÖØ-öø-ĿŁŃ-ƶ0-9_]*/g, // Include unicode special characters in words
            (txt) => {
                if (isRomanNumeral(txt)) {
                    return txt.toUpperCase();
                }

                return (
                    txt.charAt(0).toUpperCase() + preserveCapitalLetters(txt.substr(1))
                );
            }
        );
    }

    return '';
}

export function toPascalCase(str) {
    if (str) {
        const camelCaseStr = camelCase(str);
        return camelCaseStr.charAt(0).toUpperCase() + camelCaseStr.slice(1);
    }
    return '';
}

export function htmlToPlainText(html) {
    if (html) {
        return html.replace(/<\/?[^>]+(>|$)/g, ' ');
    }
    return '';
}

export function formatDisplayPhoneNumber(number) {
    if (!number || number.length !== 10) {
        return number;
    }
    return number.replace(/([0-9]{3})([0-9]{3})([0-9]{4})/, '($1) $2-$3');
}

// this fixes external image url to use https:// instead of http://
export function fixExternalUrls(html) {
    return html.split('src="http://').join('src="https://');
}

export function moneyFormat(val) {
    return parseFloat(val).toFixed(3).toString().slice(0, -1);
}

export function getSortOrderOfStringWithNumbers(a, b, nameKey) {
    if (a[nameKey] === b[nameKey]) {
        return 0;
    }

    let nameSort = 0;

    let aName1 = a[nameKey];
    let aName2 = '';
    let aName3 = '';
    let bName1 = b[nameKey];
    let bName2 = '';
    let bName3 = '';

    const aNumIndex = a[nameKey].search(/\d/);
    const bNumIndex = b[nameKey].search(/\d/);
    if (aNumIndex >= 0) {
        const name2String = a[nameKey].slice(aNumIndex);
        const nextNonNumIndex = name2String.search(/[^0-9]/);

        aName1 = a[nameKey].slice(0, aNumIndex);
        aName2 = parseInt(a[nameKey].slice(aNumIndex), 10);

        if (nextNonNumIndex > 0) {
            aName3 = name2String.slice(nextNonNumIndex);
        }
    }
    if (bNumIndex >= 0) {
        const name2String = b[nameKey].slice(aNumIndex);
        const nextNonNumIndex = name2String.search(/[^0-9]/);

        bName1 = b[nameKey].slice(0, aNumIndex);
        bName2 = parseInt(b[nameKey].slice(aNumIndex), 10);

        if (nextNonNumIndex > 0) {
            bName3 = name2String.slice(nextNonNumIndex);
        }
    }

    if (aName1 !== bName1) {
        nameSort = aName1 > bName1 ? 1 : -1;
    } else if (aName2 !== bName2) {
        nameSort = aName2 > bName2 ? 1 : -1;
    } else {
        nameSort = aName3 > bName3 ? 1 : -1;
    }

    return nameSort;
}

export function formatStateAbbreviations(str) {
    // try to find if there are any matches for States Abbrev first
    // regex will check for exactly two alpha chars, of any case, between parenthesis
    const regExp = /\(([A-Za-z]{2})\)/;
    const matches = regExp.exec(str);

    let newStr = str;
    if (matches) {
        // if there is a match, place what's inside with capitalized chars
        newStr = str.replace(regExp, matches[0].toUpperCase());
    }

    return newStr;
}

/**
 * Sorts an array of campsite objects by the name, making sure numbers that
 * occur after letters are sorted sequentially and not alphabetically.
 *
 * Each campsite in the array must have a name and loop key, but you can specify
 * the name of the keys by passing params. Example:
 *     campsites = [
 *         {
 *             name: 'D10',
 *             loop: 'Loop-D',
 *             ...other keys
 *         }
 *     ]
 * @param   {Array}  [campsites=[]]   - an array of campsite objects
 * @param   {String} [primaryKey='name'] - the key containing the name
 * @param   {String} [secondaryKey='loop'] - the key containing the loop
 * @returns {array} - the array of campsites sorted by name then loop
 */
export const sortCampsitesByName = (
    campsites = [],
    primaryKey = 'name',
    secondaryKey = 'loop'
) => {
    if (isEmpty(campsites)) {
        return [];
    }

    return campsites.sort((a, b) => {
        const primarySort = getSortOrderOfStringWithNumbers(a, b, primaryKey);
        const secondarySort = getSortOrderOfStringWithNumbers(a, b, secondaryKey);

        return primarySort || secondarySort;
    });
};

/* Convert input boolean value to 'Yes' or 'No' */
export const convertBoolToYN = (bool) => {
    return bool ? 'Yes' : 'No';
};

const swapDecimalPoint = (arr) => {
    const decimalArr = cloneDeep(arr);
    const pointIndex = findIndex(decimalArr, (c) => c === '.');
    if (pointIndex + 1 !== size(decimalArr)) {
        const temp = decimalArr[pointIndex];
        decimalArr[pointIndex] = decimalArr[pointIndex + 1];
        decimalArr[pointIndex + 1] = temp;
    } else {
        decimalArr[pointIndex] = '';
    }
    return decimalArr;
};

export const convertDecimalToPercent = (decimal) => {
    const decimalStr = toString(decimal);
    let decimalArr = toArray(decimalStr);

    decimalArr = swapDecimalPoint(decimalArr);
    decimalArr = swapDecimalPoint(decimalArr);

    const percent = join(decimalArr, '');

    return toNumber(percent);
};

export const maxLengthValidation = (maxLength) => (value) => {
    const result = {
        isValid: true,
    };

    if (value === '') return result;
    if (value.length > maxLength) {
        result.isValid = false;
        result.errorText = `Maximum length of ${maxLength} exceeded`;
    }

    return result;
};

export const emailValidation = (value) => {
    if (!value) return false;
    // Require at least one dot after the @ symbol to ensure there's a domain with TLD
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
        value
    );
};
